import { deckShareCrudConfig } from '@/config/objects/deck-shares';
import {
  Button,
  CrudFormView,
  FormPageLayout,
  type CrudFormViewRef,
} from '@startupquickstart/frontend';
import { ArrowLeft, Save } from 'lucide-react';
import { useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const LIST_PATH = '/presentation-shares';

/**
 * Create/edit form for a presentation share.
 *
 * Creating always lands on the detail page rather than back on the list: the
 * whole point of the form was to get a link, and the link only exists once the
 * server has minted the token.
 */
export function DeckShareFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const formRef = useRef<CrudFormViewRef>(null);

  const itemId = id === 'new' ? undefined : id;
  const isEdit = !!itemId;

  const handleSuccess = (share: { id: string }) => {
    navigate(`${LIST_PATH}/${share.id}`);
  };

  const handleCancel = () => {
    navigate(isEdit ? `${LIST_PATH}/${itemId}` : LIST_PATH);
  };

  const handleSave = () => {
    formRef.current?.submit();
  };

  const actions = (
    <div className="flex items-center gap-3">
      <Button type="button" variant="ghost" onClick={handleCancel}>
        Cancel
      </Button>
      <Button type="button" onClick={handleSave} loading={formRef.current?.isPending} icon={Save}>
        {isEdit ? 'Save Changes' : 'Create Link'}
      </Button>
    </div>
  );

  return (
    <FormPageLayout>
      <FormPageLayout.Header>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleCancel}
            aria-label="Back"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border bg-card transition-colors hover:bg-muted"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-zinc-900">
              {isEdit ? 'Edit Share Link' : 'New Share Link'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isEdit
                ? 'Update who this link is for, or switch it off'
                : 'Create a link that opens the presentation without a password'}
            </p>
          </div>
        </div>
        {actions}
      </FormPageLayout.Header>

      <FormPageLayout.Content>
        <CrudFormView
          ref={formRef}
          config={deckShareCrudConfig}
          itemId={itemId}
          onSuccess={handleSuccess}
          renderHeader={false}
          renderFooter={false}
        />
      </FormPageLayout.Content>

      <FormPageLayout.Footer lastSaved={formRef.current?.lastSaved}>{actions}</FormPageLayout.Footer>
    </FormPageLayout>
  );
}
